import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { REQUEST } from '../../express.tokens';
import { GLOBAL_REGION } from './settings-constants';
import { CountryLanguages, CountryLanguageUrls, SettingsService } from './settings.service';

declare interface Navigator {
  languages: string[];
}
declare const navigator: Navigator;

declare interface Locale {
  region: string;
  language: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocaleService {

  private platformId = inject(PLATFORM_ID);
  private request = inject(REQUEST, { optional: true });
  private settings = inject(SettingsService);

  public readonly optimalStartPageLocale = combineLatest([
    this.settings.startPageCountryLanguages,
    this.settings.fallbackCountry,
    this.settings.fallbackLanguage,
  ]).pipe(
    map(([startPageCountryLanguages, fallbackRegion, fallbackLanguage]) => {
      return this.getOptimalLocale(startPageCountryLanguages, fallbackLanguage, fallbackRegion);
    }),
  );


  private cachedUserLocales?: Locale[];

  private getUserLocales(): Locale[] {
    if (this.cachedUserLocales) {
      return this.cachedUserLocales;
    }

    let browserLocales: string[];
    if (isPlatformServer(this.platformId) && this.request) {
      browserLocales = this.request.acceptsLanguages();
    } else {
      browserLocales = navigator.languages;
    }

    const locales = browserLocales.map(locale => {
      const [language, region] = locale.split('-');
      return {
        language,
        region,
      };
    });

    // Fill all missing regions with the first region found
    // TODO: this is not a good solution as the region is empty for most users! replace this with an ip lookup database or a "Choose your region page"
    const firstLocaleWithRegion = locales.find(locale => locale.region !== undefined);
    if (firstLocaleWithRegion !== undefined) {
      locales.filter(locale => locale.region === undefined).forEach(locale => locale.region = firstLocaleWithRegion.region);
    }

    this.cachedUserLocales = locales;
    return this.cachedUserLocales;
  }

  public getOptimalLocale(availableCountryLanguages: CountryLanguages, fallbackLanguage: string, fallbackRegion?: string): Locale {
    const userLocales = this.getUserLocales();

    if (availableCountryLanguages[GLOBAL_REGION]) {
      return {
        language: this.getOptimalLanguage(availableCountryLanguages[GLOBAL_REGION], fallbackLanguage),
        region: GLOBAL_REGION,
      };
    }

    for(const userLocale of userLocales) {
      if (availableCountryLanguages[userLocale.region]) {
        return {
          language: this.getOptimalLanguage(availableCountryLanguages[userLocale.region], fallbackLanguage),
          region: userLocale.region,
        };
      }
    }

    if (fallbackRegion && availableCountryLanguages[fallbackRegion]) {
      return {
        language: this.getOptimalLanguage(availableCountryLanguages[fallbackRegion], fallbackLanguage),
        region: fallbackRegion,
      };
    } else {
      const firstAvailableRegion = Object.keys(availableCountryLanguages)[0];
      return {
        language: this.getOptimalLanguage(availableCountryLanguages[firstAvailableRegion], fallbackLanguage),
        region: firstAvailableRegion,
      };
    }
  }

  public getOptimalLanguage(availableLanguages: string[], fallbackLanguage: string): string {
    availableLanguages = availableLanguages.map((language) => language.toLowerCase()); // This is obsolete once FirstSpirit generated the languages in lowercase, already fixed in templates

    const userLocales = this.getUserLocales();
    const userLanguages = userLocales.map(locale => locale.language);

    for(const userLanguage of userLanguages) {
      if (availableLanguages.includes(userLanguage)) {
        return userLanguage;
      }
    }

    if (availableLanguages.includes(fallbackLanguage)) {
      return fallbackLanguage;
    } else {
      return availableLanguages[0];
    }
  }

  public convertCountryLanguageUrlsToCountryLanguages(countryLanguageUrls: CountryLanguageUrls): CountryLanguages {
    const availableCountryLanguages: CountryLanguages = {};
    for (const [country, countryLanguages] of Object.entries(countryLanguageUrls)) {
      availableCountryLanguages[country] = Object.keys(countryLanguages);
    }
    return availableCountryLanguages;
  }

}
